import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { QueryTag, TagList } from '@casops/store/api';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import {
  AssetGroupModel,
  useListAssetGroupsQuery,
  useDeleteAssetGroupMutation,
} from '@casops/store/apis/assetGroupsApi';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';

type Model = AssetGroupModel;

const modelName = ModelName.AssetGroup;
const sidePanel = SidePanel.ManageAssetGroup;
const permission = Permission.AssetManagement;

const getItemName = (data: Model): string => data.name;

const canDeleteItem = (data: Model): boolean => !data.used;

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteAssetGroupMutation,
    getItemName,
  },
};

const tagsToClearOnRefresh = [{ type: QueryTag.AssetGroup, id: TagList }];

export function AssetGroupsPage() {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.AssetGroupName'), minWidth: 100 },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
      { field: 'totA', headerName: t('Fields.AssetCount'), minWidth: 80 },
      { field: 'totG', headerName: t('Fields.GeofenceCount'), minWidth: 80 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListAssetGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Model.Collection.Title', { modelName })}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={modelName}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        actions={
          <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
        }
      />
    </StandardPage>
  );
}

export default AssetGroupsPage;
